import React, { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  CircularProgress,
  Link,
} from '@mui/material';

const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Public invoice payment (signed token from email). No login.
 * Backend uses the same Authorize.Net flow as authenticated /payments/process/.
 */
export default function PayInvoice() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    card_number: '',
    expiry_month: '',
    expiry_year: '',
    cvv: '',
    cardholder_name: '',
  });

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Missing payment link. Use the “Pay invoice” button from your invoice email.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${apiBase}/payments/public/pay-info/`, {
          params: { token },
        });
        if (cancelled) return;
        setInfo(res.data);
        setFormData((prev) => ({
          ...prev,
          amount: res.data.amount_due ?? '',
        }));
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || 'Unable to load invoice.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${apiBase}/payments/public/guest-process/`, {
        token,
        amount: formData.amount,
        card_data: {
          card_number: formData.card_number.replace(/\s/g, ''),
          expiry_month: parseInt(formData.expiry_month, 10),
          expiry_year: parseInt(formData.expiry_year, 10),
          cvv: formData.cvv,
          cardholder_name: formData.cardholder_name,
        },
      });
      setSuccess(true);
    } catch (err) {
      const d = err.response?.data;
      setError(d?.details || d?.error || 'Payment failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4 }}>
      <Container maxWidth="sm">
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#1E3A5F' }}>
          Pay invoice
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Secure card payment. You do not need to log in when using the link from your email.
        </Typography>

        {error && !info && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <Alert severity="success">
            Payment submitted successfully. Thank you. If you have an account, you can also{' '}
            <Link component={RouterLink} to="/login">
              log in
            </Link>{' '}
            to view invoices.
          </Alert>
        ) : info && !info.payable ? (
          <Alert severity="info">{info.message || 'This invoice cannot be paid online.'}</Alert>
        ) : info ? (
          <Paper sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              {info.company_name}
            </Typography>
            <Typography variant="body2">Invoice {info.invoice_number}</Typography>
            <Typography variant="body2">Due {info.due_date}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Amount due: <strong>${info.amount_due}</strong>
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="amount"
                    label="Amount (USD)"
                    type="number"
                    inputProps={{
                      step: '0.01',
                      min: '0.01',
                      max: info.amount_due,
                    }}
                    value={formData.amount}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="cardholder_name"
                    label="Name on card"
                    value={formData.cardholder_name}
                    onChange={handleChange}
                    autoComplete="cc-name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="card_number"
                    label="Card number"
                    value={formData.card_number}
                    onChange={handleChange}
                    autoComplete="cc-number"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    required
                    name="expiry_month"
                    label="Exp. month (MM)"
                    value={formData.expiry_month}
                    onChange={handleChange}
                    inputProps={{ maxLength: 2 }}
                    autoComplete="cc-exp-month"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    required
                    name="expiry_year"
                    label="Exp. year (YYYY)"
                    value={formData.expiry_year}
                    onChange={handleChange}
                    inputProps={{ maxLength: 4 }}
                    autoComplete="cc-exp-year"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="cvv"
                    label="CVV"
                    type="password"
                    value={formData.cvv}
                    onChange={handleChange}
                    inputProps={{ maxLength: 4 }}
                    autoComplete="cc-csc"
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ mt: 2, py: 1.25 }}
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Pay now'}
              </Button>
            </Box>
          </Paper>
        ) : null}

        <Typography variant="caption" display="block" sx={{ mt: 3, color: 'text.secondary' }}>
          <Link component={RouterLink} to="/login">
            Customer login
          </Link>
        </Typography>
      </Container>
    </Box>
  );
}
